import React from 'react';
import {
  customers,
  modals,
  admins,
  customerOauthConfig as customerOauthConfigDuck
} from "../../../ducks";
import duckify, {filterDuck, singleDuck} from "../../../utils/duckify";
import PageHeader from "../../PageHeader";
import ContactList from "../../ContactList/ContactList";
import FALabel from "../../shared/FALabel";
import LinkButton from "../../LinkButton/LinkButton";
import ModalRoot from "../../ModalRoot";
import EditAdminModal from "./EditAdminModal";
import './styles/PermissionGroups.scss';
import flags from "../../../../../config/admin_flags.json";
import {compose} from "redux";
import withFiltering from "../../../hocs/withFiltering";
import withPagination from "../../../hocs/withPagination";
import withSorting from "../../../hocs/withSorting";
import Filters from "../../Filters/Filters";
import Pagination from "react-ultimate-pagination-bootstrap-4";
import {bulkCheckboxColumn, defaultColumn} from "../../ContactList/ColumnDefinitions";
import levels from "../../../../../config/admin_levels.json";
import InfoTooltip from "../../shared/InfoTooltip";
import {apiFetch} from "../../../utils";
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import useBulkActions from "../../../hooks/useBulkActions";
import BulkAction from "../../ContactList/BulkActions/BulkAction";
import Edit from "../../ContactList/BulkActions/Edit";
import BulkActions from "../../ContactList/BulkActions/BulkActions";
import {faPencil} from "@fortawesome/pro-solid-svg-icons/faPencil";

const schoolName = (schoolId, schoolList) => {
  const school = schoolList.find(x => x.id === schoolId)
  return school ? school.name : '';
}

export const allowedSchoolDisplay = (admin, schoolList) => {
  if (!admin.schools_restricted) {
    return 'All Schools';
  } else if (admin.allowed_schools.length === 0) {
    return 'No Schools';
  } else if (admin.allowed_schools.length > 1) {
    return <OverlayTrigger
      placement='right'
      overlay={(
        <Tooltip id={`tooltip-${admin.id}`}>
          {admin.allowed_schools.map(schoolId => schoolName(schoolId, schoolList)).join(', ')}
        </Tooltip>
      )}>
      <span>{'Multiple Schools'}</span>
    </OverlayTrigger>
  } else {
    return schoolName(admin.allowed_schools[0], schoolList);
  }
}

const permissionGroupDisplay = (admin) => {
  return <OverlayTrigger
    placement='right'
    overlay={(
      <Tooltip id={`tooltip-${admin.id}`}>
        {flags.filter(flag => admin[flag.canonical_name]).map(flag => flag.name).join(', ') || 'No permissions'}
      </Tooltip>
    )}>
    <span>{admin.permission_group || 'Custom Permissions'}</span>
  </OverlayTrigger>
}

const BulkEditAdmin = ({
                         showModal,
                         icon,
                         title,
                         ...rest
                       }) => {
  return (
    <BulkAction
      icon={icon}
      title={title}
      onClick={() => showModal('EditAdminModal', {
        title: title,
        ...rest
      })}
      {...rest}
    />
  );
}

const AdminListPage = ({
  admins,
  currentCustomer,
  onEditAdmin,
  onDeleteAdmin,
  onResetAdmin,
  showAudits,
  filters,
  onSort,
  page,
  setFilters,
  setPage,
  sort,
  oauthConfig,
  subsites,
  showModal,
  bulkOnSave
}) => {
  const filteredFlags = currentCustomer.review_enabled ? flags : flags.filter(flag => flag.canonical_name !== 'reader_permitted');
  const hasOauth = oauthConfig && oauthConfig.oauth_provider;
  const schoolList = [currentCustomer].concat(subsites);

  const { trackedItems,
    selectionModeCount,
    promptSelectAll,
    setPromptSelectAll,
    selectionMode,
    togglePageTracked,
    toggleTrackedItem,
    itemIsSelected,
    selectAllItems } = useBulkActions(admins.filter(admin => !admin.source));

  const columns = [
    bulkCheckboxColumn({
      value: admin => !admin.source,
      promptSelectAll,
      togglePageTracked,
      selectionMode,
      itemIsSelected,
      toggleTrackedItem
    }),
    defaultColumn({
      header: 'Email',
      sortName: 'auths.email',
      value: 'email',
      columnClassNames: 'admin-accounts-email-cell'
    }),
    defaultColumn({
      header: 'First Name',
      sortName: 'auths.first_name',
      value: 'first_name',
    }),
    defaultColumn({
      header: 'Last Name',
      sortName: 'auths.last_name',
      value: 'last_name',
    }),
    defaultColumn({
      header: 'Type',
      sortName: 'user_type',
      value: admin => <td>{levels[admin.acl]}</td>,
    }),
    currentCustomer.super_id && defaultColumn({
      header: 'Source',
      sortName: 'customer_id',
      value: 'source'
    }),
    subsites.length > 0 && defaultColumn({
      header: 'Schools',
      value: admin => <td>{allowedSchoolDisplay(admin, schoolList)}</td>
    }),
    defaultColumn({
      header: 'Permission Group',
      sortName: 'permission_groups.name',
      value: admin => <td>{permissionGroupDisplay(admin)}</td>
    }),
    defaultColumn({
      header: '',
      value: admin => !!admin.source ? <td /> : (
        <td>
          <LinkButton onClick={() => onEditAdmin(admin, filteredFlags, false, schoolList)} content="Edit" />{' | '}
          <LinkButton onClick={() => showAudits(admin.id)} content="View Changes" />{' | '}
          <LinkButton onClick={() => onDeleteAdmin(admin)} content="Delete" />
          {(admin.acl !== 4) && (!oauthConfig || !oauthConfig.oauth_provider) && (<>
            {' | '}
            <LinkButton onClick={() => onResetAdmin(admin)} content="Reset Password" />
          </>)}
        </td>
      )
    })
  ].filter(val => !!val);

  const totalPages = admins ? Math.ceil(admins.total / admins.perPage) : 0;

  const selectedAdminIds = admins.filter(item => itemIsSelected(item.id)).map(admin => admin.id);

  const setGroupInitialValues = () => {
    const initialValues = {admin_ids: selectedAdminIds, permission_group_id: null};
    filteredFlags.forEach(flag => initialValues[flag.canonical_name] = false);
    return initialValues;
  }

  return <React.Fragment>
    <PageHeader header="Admin Accounts" />

    <BulkActions
      search={admins.search}
      contactTotal={admins.total}
      adminIds={selectedAdminIds}
      showModal={(name, props) => showModal(name, props)}
    >
      <Edit disabled={!admins.ready || selectionModeCount() === 0}>
        <BulkEditAdmin
          icon={faPencil}
          title='Change Permissions'
          onSave={bulkOnSave('admins/bulk_update_group')}
          initialValues={setGroupInitialValues()}
          flags={filteredFlags}
        />
        <BulkEditAdmin
          icon={faPencil}
          title='Change Type'
          onSave={bulkOnSave('admins/bulk_update_type')}
          initialValues={{admin_ids: selectedAdminIds, acl:  2}}
        />
        {subsites.length > 0 && (
          <BulkEditAdmin
            icon={faPencil}
            title='Add Schools'
            onSave={bulkOnSave('admins/bulk_add_schools')}
            initialValues={{admin_ids: selectedAdminIds, schools_restricted: true, allowed_schools: []}}
          />
        )}
        {subsites.length > 0 && (
          <BulkEditAdmin
            icon={faPencil}
            title='Remove Schools'
            onSave={bulkOnSave('admins/bulk_remove_schools')}
            initialValues={{admin_ids: selectedAdminIds, schools_restricted: true ,allowed_schools: []}}
          />
        )}
      </Edit>
    </BulkActions>

    <Filters
      fields={[{
        solr_prompt: 'text',
        display_name: 'Search',
        filter_type: 'TextFilter',
        tooltip: (<InfoTooltip preNbsp>
          Search for text in email, first name, or last name.
        </InfoTooltip>)
      }]}
      selected={filters}
      selectCount={selectionModeCount()}
      count={admins.total}
      submitFilters={setFilters}
      itemsName='Admins'
    />

    <div className='permission-group-list-add'>
      <LinkButton
        className="add-item-button"
        content={<FALabel icon="plus-circle" iconFirst label=" Add a new admin" />}
        onClick={() => onEditAdmin(undefined, filteredFlags, !hasOauth, schoolList)}
      />
    </div>

    {totalPages > 1 && (
      <div className='contact-list-react-pagination'>
        <div className='pagination pagination-right'>
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            hideFirstAndLastPageLinks
            onChange={(newPage) => {
              setPage(newPage);
              setPromptSelectAll(false);
            }}
          />
        </div>
      </div>
    )}

    <ContactList
      className="table-hover permission-group-table"
      columns={columns}
      contacts={admins}
      emptyMessage="No Admins"
      onSort={onSort}
      sort={sort}
    />

    <ModalRoot modals={{ EditAdminModal }} />
  </React.Fragment>;
};

const mapDucksToProps = ({filters, sort, page, perPage}) => ({
  admins: filterDuck(admins, {filters, sort, page: page || 1, per_page: perPage, exclude_superadmins: true, include_blocked_users: true}),
  currentCustomer: singleDuck(customers, 'current'),
  oauthConfig: customerOauthConfigDuck,
  subsites: filterDuck(customers, {list_subsites: true})
});

const mapDispatchToProps = (dispatch) => ({
  onEditAdmin: (adminToEdit, filteredFlags, showPassword, schoolList) => {
    const admin = adminToEdit || {}
    const initialValues = {id: admin.id, first_name: admin.first_name, last_name: admin.last_name, email: admin.email, acl: admin.acl === 0 ? admin.acl : (admin.acl || 2), permission_group_id: admin.permission_group_id, schools_restricted: admin.id ? admin.schools_restricted : false, allowed_schools: admin.schools_restricted ? admin.allowed_schools || [] : schoolList.map(school => school.id)}
    flags.forEach(flag => initialValues[flag.canonical_name] = admin[flag.canonical_name])

    return dispatch(modals.actions.show('EditAdminModal', {
      admin: admin,
      flags: filteredFlags,
      showPassword: showPassword,
      initialValues: initialValues,
      title: admin.id ? 'Edit Admin' : 'Add Admin',
      onSave: admin => dispatch(admins.actions.save(admin)).then(() => window.location.reload())
    }))
  },
  bulkOnSave: path => {
    return (values) => apiFetch.post(
      {},
      path,
      { values }
    ).then(() => dispatch(admins.actions.markDirty()))
  },
  onDeleteAdmin: admin => dispatch(modals.actions.show('VerificationModal', {
    confirmButtonType: 'primary',
    message: `Are you sure you want to delete "${admin.name}"?`,
    confirmText: 'Delete',
    onConfirm: () => dispatch(admins.actions.remove(admin))
      .then(() => dispatch(admins.actions.markDirty()))
      .catch(() => {
        Utility.displayFlashMessages({ error: `${admin.name} could not be deleted` });
      }),
    title: 'Delete Admin',
    onClose: () => dispatch(modals.actions.hide()),
  })),
  onResetAdmin: admin => dispatch(modals.actions.show('VerificationModal', {
    confirmButtonType: 'primary',
    message: `This will send ${admin.email} an email allowing them to reset their password. Are you sure you wish to proceed?`,
    confirmText: 'Confirm',
    onConfirm: () => apiFetch.put({},`admins/${admin.id}/reset_password`, {})
      .then(() => Utility.displayFlashMessages({success: `Password reset email sent to ${admin.email}.`}))
      .catch(response => {
        Utility.displayFlashMessages({ error: `Failed to send reset password email${response.error ? `: ${response.error}` : ''}` });
      }),
    title: 'Reset Admin Password',
    onClose: () => dispatch(modals.actions.hide()),
  })),
  showAudits: (id, resource = 'users') => dispatch(modals.actions.show('AuditViewer', {
    id,
    resource
  })),
  showModal: (name, props) => dispatch(modals.actions.show(name, props))
});

export default compose(
  withFiltering,
  withPagination,
  withSorting({column: 'auths.email', order: 'asc'}),
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps)
)(AdminListPage);
